import { useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  sankey,
  sankeyLeft,
  sankeyLinkHorizontal,
  type SankeyGraph,
  type SankeyNode as DSNode,
  type SankeyLink as DSLink,
} from "d3-sankey";
import type { RunDetail } from "@/api/types";
import { EmptyState } from "@/components/empty-state";
import { CHECK_PALETTE, RUN_COLORS } from "@/lib/run-colors";

/**
 * Sankey of one run's domain flow.
 *
 *   Input ─┬→ Pre-filtered
 *          └→ Targets ─┬→ Pending
 *                      ├→ Unreachable           target server didn't respond (DNS/network/TLS/timeout)
 *                      ├→ Internal error        worker crash / data-integrity failure not under any check
 *                      └→ Evaluated ─┬→ No match
 *                                    ├→ [Check A]  ─┬→ Pushed to Close
 *                                    ├→ [Check B]  ─┼→ Duplicate          (extracted but lead already in Close)
 *                                    ├→ [Check C]  ─┼→ Errored (extract)  (tag fired then extract failed)
 *                                    │              └→ Skipped (no lead)  (no impressum / LLM rejected)
 *                                    └→ [Skip-act] ──→ Skipped (by rule)  (SKIP-action check matched)
 *
 * Colors come from `RUN_COLORS` so each node matches the corresponding
 * outcome-bar segment / progress-chart band.
 *
 * Skip-action checks (e.g. "bad words", "JTL Shop") are rendered as
 * first-class nodes and route 100% of their matched count to the
 * "Skipped (by rule)" sink. With them surfaced here, "No match" now
 * matches the outcome bar's stricter definition (no rule fired at all).
 *
 * Built on `d3-sankey` with `sankeyLeft` alignment so each node sits at its
 * earliest depth — Pre-filtered ends up next to Input, Pending next to
 * Targets, etc. (`recharts`'s Sankey only supports "justify" alignment which
 * crammed every sink into the rightmost column.)
 *
 * Link widths are √-scaled. A real-world run is dominated by Pending,
 * Unreachable, and No-match (each often >40% of the total); on a linear
 * scale the small-but-interesting flows (Pushed / Duplicate / Errored /
 * Skipped-by-rule) render as hairlines. sqrt compresses the giants
 * without changing their ordering, so the funnel reads as a funnel. Labels
 * and tooltips always show the real counts.
 */

const TONES: Record<string, string> = {
  Input: RUN_COLORS.input,
  "Pre-filtered": RUN_COLORS.preFiltered,
  Targets: RUN_COLORS.input,
  Pending: RUN_COLORS.pending,
  Evaluated: RUN_COLORS.evaluated,
  "No match": RUN_COLORS.noMatch,
  Unreachable: RUN_COLORS.unreachable,
  "Internal error": RUN_COLORS.error,
  "Errored (extract)": RUN_COLORS.error,
  "Skipped (no lead)": RUN_COLORS.tagged,
  "Skipped (by rule)": RUN_COLORS.tagged,
  "Pushed to Close": RUN_COLORS.pushed,
  Duplicate: RUN_COLORS.duplicate,
};

interface NodeDatum {
  name: string;
  color: string;
  // Real (un-scaled) count; what humans actually want to read.
  realValue: number;
}
interface LinkDatum {
  source: number;
  target: number;
  // sqrt-scaled width used by the d3-sankey layout (preserves ordering,
  // compresses the giant Pending / Unreachable / No-match slabs so the
  // small Pushed / Duplicate / Errored flows are still visible).
  value: number;
  // Real (un-scaled) count; what tooltips and labels display.
  realValue: number;
}
interface SankeyData {
  nodes: NodeDatum[];
  links: LinkDatum[];
}

const clampNonNeg = (n: number) => (n > 0 ? n : 0);

export function buildData(run: RunDetail): SankeyData | null {
  const input = run.input_count;
  const preFiltered = run.skipped;
  const targets = run.total_targets;

  const by = run.progress.by_status;
  const pending = by.pending ?? 0;
  const unreachable = by.unreachable ?? 0;
  const errorTotal = by.error ?? 0;
  const skippedTotal = by.skipped ?? 0;

  const checks = Object.entries(run.progress.by_primary_check ?? {})
    .map(([name, c]) => {
      const matched = c.matched;
      const extracted = c.extracted;
      const errored = c.errored;
      const pushed = c.pushed;
      const isSkipAction = c.action_kind === "skip";
      const duplicate = clampNonNeg(extracted - pushed);
      // For SKIP-action checks: matched - extracted - errored == matched
      // by construction (extracted/errored are 0), and the whole flow
      // routes to "Skipped (by rule)" — kept as a separate field so the
      // link-building code below can render distinct sinks.
      const skippedNoLead = isSkipAction
        ? 0
        : clampNonNeg(matched - extracted - errored);
      const skippedByRule = isSkipAction ? matched : 0;
      return { name, matched, pushed, errored, duplicate, skippedNoLead, skippedByRule };
    })
    .filter((c) => c.matched > 0)
    .sort((a, b) => b.matched - a.matched);

  const erroredPostTag = checks.reduce((s, c) => s + c.errored, 0);
  // ERROR is now strictly internal (extract crash / worker exception). The
  // residual after subtracting per-check errored is the worker-crash bucket
  // — rare, but visible as "Internal error" coming off Targets directly.
  const internalError = clampNonNeg(errorTotal - erroredPostTag);

  const checksAccountedSkipped = checks.reduce(
    (s, c) => s + c.skippedNoLead + c.skippedByRule, 0,
  );
  const noMatch = clampNonNeg(skippedTotal - checksAccountedSkipped);

  const evaluatedTotal =
    noMatch + checks.reduce((s, c) => s + c.matched, 0);

  if (input === 0 && targets === 0) return null;

  const nodes: NodeDatum[] = [];
  const idx = (name: string, color: string) => {
    const existing = nodes.findIndex((n) => n.name === name);
    if (existing >= 0) return existing;
    nodes.push({ name, color, realValue: 0 });
    return nodes.length - 1;
  };

  const inputI = idx("Input", TONES.Input);
  const preI = idx("Pre-filtered", TONES["Pre-filtered"]);
  const targetsI = idx("Targets", TONES.Targets);
  const pendingI = idx("Pending", TONES.Pending);
  const unreachableI = idx("Unreachable", TONES.Unreachable);
  const internalErrorI = idx("Internal error", TONES["Internal error"]);
  const evaluatedI = idx("Evaluated", TONES.Evaluated);
  const noMatchI = idx("No match", TONES["No match"]);
  const pushedI = idx("Pushed to Close", TONES["Pushed to Close"]);
  const duplicateI = idx("Duplicate", TONES.Duplicate);
  const erroredExtractI = idx("Errored (extract)", TONES["Errored (extract)"]);
  const skippedNoLeadI = idx("Skipped (no lead)", TONES["Skipped (no lead)"]);
  const skippedByRuleI = idx("Skipped (by rule)", TONES["Skipped (by rule)"]);

  const links: LinkDatum[] = [];
  const push = (source: number, target: number, value: number) => {
    links.push({ source, target, value, realValue: value });
  };
  push(inputI, preI, preFiltered);
  push(inputI, targetsI, targets);
  push(targetsI, pendingI, pending);
  push(targetsI, unreachableI, unreachable);
  push(targetsI, internalErrorI, internalError);
  push(targetsI, evaluatedI, evaluatedTotal);
  push(evaluatedI, noMatchI, noMatch);

  for (let i = 0; i < checks.length; i++) {
    const c = checks[i];
    const color = CHECK_PALETTE[i % CHECK_PALETTE.length];
    const checkI = idx(c.name, color);
    push(evaluatedI, checkI, c.matched);
    push(checkI, pushedI, c.pushed);
    push(checkI, duplicateI, c.duplicate);
    push(checkI, erroredExtractI, c.errored);
    push(checkI, skippedNoLeadI, c.skippedNoLead);
    push(checkI, skippedByRuleI, c.skippedByRule);
  }

  // Drop zero-value links and any orphan nodes; re-index in a final pass.
  const usedNodeIdxs = new Set<number>();
  for (const l of links) {
    if (l.value > 0) {
      usedNodeIdxs.add(l.source);
      usedNodeIdxs.add(l.target);
    }
  }
  if (usedNodeIdxs.size === 0) return null;

  const surviving = nodes
    .map((n, i) => ({ ...n, oldIdx: i }))
    .filter((n) => usedNodeIdxs.has(n.oldIdx));
  const remap = new Map(surviving.map((n, newIdx) => [n.oldIdx, newIdx]));

  // Compute real per-node counts before sqrt-transforming the links.
  // Use incoming sum for non-source nodes, outgoing for sources — both
  // equal the real value by sankey-conservation. We can't read this back
  // off `node.value` after layout because the layout sees sqrt'd values.
  const realByOld = new Map<number, { inc: number; out: number }>();
  for (const l of links) {
    if (l.value <= 0) continue;
    const inc = realByOld.get(l.target) ?? { inc: 0, out: 0 };
    inc.inc += l.realValue;
    realByOld.set(l.target, inc);
    const out = realByOld.get(l.source) ?? { inc: 0, out: 0 };
    out.out += l.realValue;
    realByOld.set(l.source, out);
  }

  return {
    nodes: surviving.map(({ name, color, oldIdx }) => {
      const r = realByOld.get(oldIdx) ?? { inc: 0, out: 0 };
      return { name, color, realValue: r.inc > 0 ? r.inc : r.out };
    }),
    links: links
      .filter((l) => l.value > 0)
      .map((l) => ({
        source: remap.get(l.source)!,
        target: remap.get(l.target)!,
        // sqrt compresses the dominant flows so small ones stay visible.
        // Floor at 1 so a "1 pushed" link still has a clickable hairline.
        value: Math.max(1, Math.sqrt(l.realValue)),
        realValue: l.realValue,
      })),
  };
}

function useElementWidth<T extends HTMLElement>(): [React.RefObject<T>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    if (!ref.current) return;
    setWidth(ref.current.clientWidth);
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setWidth(w);
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

const RIGHT_GUTTER = 160;
const TOP_PAD = 14;
const BOTTOM_PAD = 14;

type GraphNode = DSNode<NodeDatum, LinkDatum>;
type GraphLink = DSLink<NodeDatum, LinkDatum>;

interface HoverState {
  x: number;
  y: number;
  label: string;
}

export function RunSankey({ run, height = 520 }: { run: RunDetail; height?: number }) {
  const data = useMemo(() => buildData(run), [run]);
  const [containerRef, width] = useElementWidth<HTMLDivElement>();
  const [hover, setHover] = useState<HoverState | null>(null);

  if (!data) {
    return (
      <EmptyState
        title="Nothing to chart yet"
        hint="The flow chart fills in as domains start finishing."
      />
    );
  }

  return (
    <div ref={containerRef} className="relative" style={{ height }}>
      {width > 0 ? (
        <SankeyChart data={data} width={width} height={height} onHover={setHover} />
      ) : null}
      {hover ? (
        <div
          className="pointer-events-none absolute z-10 rounded border px-2 py-1 text-xs whitespace-nowrap"
          style={{
            left: Math.min(hover.x + 12, width - 220),
            top: Math.max(hover.y - 28, 0),
            background: "hsl(222 47% 11%)",
            borderColor: "hsl(217 33% 30%)",
            color: "hsl(210 40% 96%)",
          }}
        >
          {hover.label}
        </div>
      ) : null}
    </div>
  );
}

interface SankeyChartProps {
  data: SankeyData;
  width: number;
  height: number;
  onHover: (h: HoverState | null) => void;
}

function SankeyChart({ data, width, height, onHover }: SankeyChartProps) {
  const { nodes, links } = useMemo(() => {
    const layout = sankey<NodeDatum, LinkDatum>()
      .nodeAlign(sankeyLeft)
      .nodeWidth(12)
      .nodePadding(20)
      .extent([
        [1, TOP_PAD],
        [Math.max(width - RIGHT_GUTTER, 100), height - BOTTOM_PAD],
      ]);

    const graph: SankeyGraph<NodeDatum, LinkDatum> = {
      nodes: data.nodes.map((n) => ({ ...n })),
      links: data.links.map((l) => ({ ...l })),
    };
    return layout(graph);
  }, [data, width, height]);

  const linkPath = useMemo(() => sankeyLinkHorizontal<NodeDatum, LinkDatum>(), []);

  return (
    <svg
      width={width}
      height={height}
      role="img"
      onMouseLeave={() => onHover(null)}
    >
      <g fill="none" strokeOpacity={0.45}>
        {links.map((link, i) => {
          const sourceName = (link.source as GraphNode).name;
          const targetName = (link.target as GraphNode).name;
          return (
            <path
              key={i}
              d={linkPath(link as GraphLink) ?? ""}
              stroke="hsl(220 14% 50%)"
              strokeWidth={Math.max(1, link.width ?? 1)}
              onMouseMove={(e) =>
                onHover({
                  x: e.nativeEvent.offsetX,
                  y: e.nativeEvent.offsetY,
                  label: `${sourceName} → ${targetName}: ${(link as unknown as LinkDatum).realValue.toLocaleString()}`,
                })
              }
            />
          );
        })}
      </g>
      <g>
        {nodes.map((node, i) => (
          <SankeyNode
            key={i}
            node={node as GraphNode}
            containerWidth={width}
            onHover={onHover}
          />
        ))}
      </g>
    </svg>
  );
}

interface SankeyNodeViewProps {
  node: GraphNode;
  containerWidth: number;
  onHover: (h: HoverState | null) => void;
}

function SankeyNode({ node, containerWidth, onHover }: SankeyNodeViewProps) {
  const x0 = node.x0 ?? 0;
  const x1 = node.x1 ?? 0;
  const y0 = node.y0 ?? 0;
  const y1 = node.y1 ?? 0;
  const w = Math.max(x1 - x0, 1);
  const h = Math.max(y1 - y0, 1);
  const labelOnRight = x1 + RIGHT_GUTTER < containerWidth + 4;
  const labelX = labelOnRight ? x1 + 8 : x0 - 8;
  const labelY = (y0 + y1) / 2;
  const real = node.realValue ?? 0;
  return (
    <g
      onMouseMove={(e) =>
        onHover({
          x: e.nativeEvent.offsetX,
          y: e.nativeEvent.offsetY,
          label: `${node.name}: ${real.toLocaleString()}`,
        })
      }
    >
      <rect
        x={x0}
        y={y0}
        width={w}
        height={h}
        fill={node.color}
        stroke="hsl(222 47% 6%)"
        strokeWidth={1}
      />
      <text
        x={labelX}
        y={labelY}
        dy="0.35em"
        textAnchor={labelOnRight ? "start" : "end"}
        fontSize={12}
        fontWeight={500}
        fill="hsl(210 40% 96%)"
      >
        {node.name}
        <tspan fill="hsl(220 14% 70%)" dx={6} fontWeight={400}>
          {real.toLocaleString()}
        </tspan>
      </text>
    </g>
  );
}
